<script>
import { mapGetters, mapActions } from "vuex";
import { useVuelidate } from '@vuelidate/core'
import { required, email } from '@vuelidate/validators'

export default {
  setup () {
    return { v$: useVuelidate() }
  },
  computed: {
    ...mapGetters("auth", { getCurrentOrganisation: "getCurrentOrganisation" }),

    supplier: {
      get() {
        return {
          id: this.id,
          archived: this.archived,
          contact_number: this.contact_number,
          contact_person: this.contact_person,
          email: this.email,
          name: this.name,
          notes: this.notes,
          organisation: this.getCurrentOrganisation.id,
          supplier_group: this.supplier_group,
          tax_rate: this.tax_rate,
          vat_number: this.vat_number,

          addresses: this.addresses,

          created_at: this.created_at,
          created_by: this.created_by,
          edited_at: this.edited_at,
          edited_by: this.edited_by,
        };
      },
      set(value) {
        //this.id = value.id
        this.archived = value.archived ?? this.archived;
        this.contact_number = value.contact_number ?? this.contact_number;
        this.contact_person = value.contact_person ?? this.contact_person;
        this.email = value.email ?? this.email;
        this.name = value.name ?? this.name;
        this.notes = value.notes ?? this.notes;
        this.organisation = value.organisation ?? this.organisation;
        this.supplier_group = value.supplier_group ?? this.supplier_group;
        this.tax_rate = value.tax_rate ?? this.tax_rate;
        this.vat_number = value.vat_number ?? this.vat_number;

        this.addresses = value.addresses ?? this.addresses;

        this.created_at = value.created_at ?? this.created_at;
        this.created_by = value.created_by ?? this.created_by;
        this.edited_at = value.edited_at ?? this.edited_at;
        this.edited_by = value.edited_by ?? this.edited_by;
      },
    },

    unsavedChanges() {
      if (!this.initializedObject || !this.supplier) return true;

      const keysToCompare = [
        "contact_person",
        "contact_number",
        "email",
        "name",
        "supplier_group.id",
        "tax_rate.id",
        "vat_number",
        "notes"
      ];

      const hasChanges = keysToCompare.some((key) => {
        const initialValue = key.split(".").reduce((acc, part) => acc && acc[part], this.initializedObject);
        const currentValue = key.split(".").reduce((acc, part) => acc && acc[part], this.supplier);
        return JSON.stringify(initialValue) !== JSON.stringify(currentValue);
      });

      if (hasChanges) return true;

      return this.supplier?.addresses?.length !== this.initializedObject?.addresses?.length || 
        this.supplier?.addresses?.some((item) => {
          const originalItem = this.initializedObject?.addresses?.find((i) => i.id === item.id);
          if (!originalItem) return true;

          const getNestedValue = (obj, path) => {
            return path.split('.').reduce((acc, key) => acc?.[key] ?? null, obj);
          };

          const itemKeys = [
            "archived",
            "name",
            "address_one",
            "address_two",
            "city",
            "country",
            "postal_code",
            "province",
            "suburb",
          ];

          return itemKeys.some((key) => {
            const initialValue = getNestedValue(originalItem, key);
            const currentValue = getNestedValue(item, key);

            return initialValue != currentValue;
          });
        });
    },
  },
  data() {
    return {
      //id: null,
      archived: false,
      contact_number: null,
      contact_person: null,
      email: null,
      name: null,
      notes: null,
      organisation: null,
      supplier_group: null,
      tax_rate: null,
      vat_number: null,

      addresses: [],

      created_at: null,
      created_by: null,
      edited_at: null,
      edited_by: null,

      modelName: "Supplier",

      initializedObject: null,
    };
  },
  methods: {
    ...mapActions("suppliers", ["fetchSupplier"]),
    ...mapActions("application", ["setIsPageLoading", "setHasUnsavedChanges"]),

    setupData(newValue) {
      this.supplier = newValue;
      this.initializedObject = {
          ...newValue,
          addresses: structuredClone(newValue.addresses),
        };
    },

    async initialize() {
      this.setIsPageLoading(true);
      try {
        if (this.id) {
          await this.fetchSupplier({
            organisation: this.getCurrentOrganisation.id,
            id: this.id,
          }).then((res) => {
            this.setupData(res.data)
          }).catch((error) => {});
        } else {
          this.archived = false;
          this.contact_number = this.newDetails?.contact_number || null;
          this.contact_person = null;
          this.email = this.newDetails?.email || null;
          this.name = this.newDetails?.name || null;
          this.notes = null;
          this.organisation = null;
          this.supplier_group = null;
          this.tax_rate = null;
          this.vat_number = this.newDetails?.vat_number || null;

          this.addresses = [];

          // this.add_address()
        }
      } finally {
        this.setIsPageLoading(false);
      }
    },
    
    add_address() {
      this.addresses.push({
        //id: null,
        archived: false,
        name: null,
        address_one: null,
        address_two: null,
        city: null,
        country: "South Africa",
        latitude: null,
        longitude: null,
        organisation: null,
        postal_code: null,
        province: null,
        suburb: null,
      });

      this.$nextTick(() => {
        const addressElements = document.querySelectorAll(".address-box");
        if (addressElements.length > 0) {
          const lastAddressElement = addressElements[addressElements.length - 1];
          
          lastAddressElement.scrollIntoView({
            behavior: "smooth",
            block: "nearest",
          });
        }
      });
    },

    remove_address(index) {
      this.addresses.splice(index, 1);
    },
  },
  mounted() {
    this.initialize();
  },
  props: {
    id: {
      type: String,
      default: null,
    },
    value: {
      type: Object,
      default(rawProps) {
        return null;
      },
    },
    newDetails: {
      type: Object,
      default(rawProps) {
        return null;
      },
    }
  },
  watch: {
    value: {
      handler(newVal) {
        this.supplier = newVal;
      },
      deep: true
    },
    supplier() {
      this.$emit("input", this.supplier);
    },
    unsavedChanges(newVal) {
      this.setHasUnsavedChanges(newVal);
    },
  },
  validations: {
    name: { required },
  },
};
</script>

<template></template>
